import React from 'react';

import { Link } from 'react-router-dom';
import { Button, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { EditorialValueCard } from '../../types/editorial-value-card.type';

interface ValueCardProps {
  card: EditorialValueCard;
}

const useStyles = makeStyles((theme) => ({
  // La carte est une colonne : l'illustration en tête, le texte au milieu, le bouton poussé en bas
  // par `marginTop: auto` pour que les boutons de la rangée s'alignent malgré des textes inégaux.
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '0.75rem',
    padding: '1.5rem 1.25rem',
    borderRadius: 12,
    backgroundColor: '#fff',
    border: '1px solid rgba(102, 118, 29, 0.16)',
  },
  illustration: {
    width: 96,
    height: 96,
    objectFit: 'contain',
  },
  lead: {
    fontFamily: 'Bitter, serif',
    fontWeight: 700,
    fontSize: '1.05rem',
    lineHeight: 1.3,
    color: theme.palette.client.primary.extraDark,
  },
  points: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  point: {
    fontSize: '0.9rem',
    lineHeight: 1.5,
    color: '#4a4a4a',
  },
  cta: {
    marginTop: 'auto',
    paddingTop: '0.5rem',
    color: theme.palette.client.accent.main,
    fontWeight: 600,
  },
}));

const ValueCard: React.FC<ValueCardProps> = ({ card }) => {
  const classes = useStyles();

  const renderCta = () => {
    if (!card.ctaLabel) return null;

    // Sans destination, le bouton reste un bouton : il ne doit produire aucun lien mort.
    if (!card.ctaPath) {
      return (
        <Button className={classes.cta} disableRipple>
          {card.ctaLabel}
        </Button>
      );
    }

    return (
      <Button className={classes.cta} component={Link} to={card.ctaPath}>
        {card.ctaLabel}
      </Button>
    );
  };

  return (
    <article className={classes.card}>
      <img className={classes.illustration} src={card.illustration} alt={card.illustrationAlt} />

      <Typography component="h3" className={classes.lead}>
        {card.lead}
      </Typography>

      {card.points && card.points.length > 0 && (
        <ul className={classes.points}>
          {card.points.map((point) => (
            <li key={point} className={classes.point}>
              {point}
            </li>
          ))}
        </ul>
      )}

      {renderCta()}
    </article>
  );
};

export default ValueCard;
